import React, { useState, useEffect, useRef } from 'react';
import { doc, deleteDoc, updateDoc } from 'firebase/firestore';
import { db, auth } from '../firebaseConfig';
import { getEmbedding } from '../utils/embeddings';
import linkifyIt from 'linkify-it';
import Link from './Link';

// Note component that handles individual note display, editing, and deletion
function Note({ note, onActive, onNoteChange, onInactive, onFindSimilar, similarNotes }) {
  // State management for note operations
  const [isDeleting, setIsDeleting] = useState(false);
  const [editedText, setEditedText] = useState(note.content);
  const [isFocused, setIsFocused] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [showDeleteDropdown, setShowDeleteDropdown] = useState(false);
  
  const textareaRef = useRef(null);
  const isUpdatingRef = useRef(false);
  const lastSavedText = useRef(note.content);
  const debouncedUpdate = useRef(null);
  const hasChangedRef = useRef(false);
  const [updateTrigger, setUpdateTrigger] = useState(0);

  // Add linkify instance
  const linkify = useRef(linkifyIt()).current;

  // Add ref for dropdown container
  const deleteDropdownRef = useRef(null);

  // Utility function to adjust textarea height to match content
  const adjustTextareaHeight = () => {
    const textarea = textareaRef.current;
    if (textarea) {
      textarea.style.height = 'auto';
      textarea.style.height = `${textarea.scrollHeight}px`;
    }
  };

  // Event handler for text changes
  const handleTextChange = (e) => {
    setEditedText(e.target.value);
    hasChangedRef.current = true;
    setIsSaving(true);
    adjustTextareaHeight();
  };

  // Main effect for handling Firebase updates
  useEffect(() => {
    if (!hasChangedRef.current || editedText === lastSavedText.current) {
      return;
    }

    debouncedUpdate.current = setTimeout(async () => {
      try {
        if (isUpdatingRef.current) {
          hasChangedRef.current = true;
          setIsSaving(true);
          return;
        }

        isUpdatingRef.current = true;
        setIsSaving(true);
        const embedding = await getEmbedding(editedText);
        const now = new Date().toISOString();
        
        // Update Firestore
        await updateDoc(doc(db, "notes_v2", note.id), {
          content: editedText,
          embedding,
          updatedAt: now
        });

        // Update Pinecone in parallel
        fetch('/api/hello', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            id: note.id,
            vector: embedding,
            metadata: {
              text: editedText,
              uid: auth.currentUser.uid,
              createdAt: now,
              type: 'note'
            }
          })
        }).catch(error => {
          console.error('Error updating Pinecone vector:', error);
        });

        lastSavedText.current = editedText;
        hasChangedRef.current = false;
        
        if (onNoteChange) {
          onNoteChange(); 
        }
      } catch (error) {
        console.error("Error updating note:", error);
      } finally {
        isUpdatingRef.current = false;
        if (!hasChangedRef.current) {
          setIsSaving(false);
        }
        
        if (hasChangedRef.current) {
          setUpdateTrigger(prev => prev + 1);
        }
      }
    }, 500);

    return () => {
      if (debouncedUpdate.current) {
        clearTimeout(debouncedUpdate.current);
      }
    };
  }, [editedText, updateTrigger]);

  // Add click outside handler
  useEffect(() => {
    function handleClickOutside(event) {
      if (deleteDropdownRef.current && !deleteDropdownRef.current.contains(event.target)) {
        setShowDeleteDropdown(false);
      }
    }

    if (showDeleteDropdown) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [showDeleteDropdown]);

  // Add position calculation function
  const getDropdownPosition = () => {
    if (!deleteDropdownRef.current) return {};
    
    const rect = deleteDropdownRef.current.getBoundingClientRect();
    const spaceBelow = window.innerHeight - rect.bottom;
    const needsFlip = spaceBelow < 120;
    
    return {
      [needsFlip ? 'bottom' : 'top']: '100%',
      right: 0,
    };
  };

  // Simplify delete click handler
  const handleDeleteClick = (e) => {
    e.stopPropagation();
    setShowDeleteDropdown(prev => !prev);
  };

  // Actual delete operation moved to a separate function
  const handleConfirmDelete = async () => {
    if (isDeleting) return;
    
    try {
      setIsDeleting(true);
      const noteRef = doc(db, "notes_v2", note.id);
      
      // Delete from Firestore
      await deleteDoc(noteRef);
      
      // Delete from Pinecone in parallel
      fetch('/api/hello', {
        method: 'DELETE',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ id: note.id })
      }).catch(error => {
        console.error('Error deleting Pinecone vector:', error);
      });
      
      if (onNoteChange) {
        onNoteChange();
      }
    } catch (error) {
      console.error("Error deleting note: ", error);
    } finally {
      setIsDeleting(false);
      setShowDeleteDropdown(false);
    }
  };

  // Handle width changes
  useEffect(() => {
    const textarea = textareaRef.current;
    if (!textarea) return;

    const resizeObserver = new ResizeObserver(() => {
      adjustTextareaHeight();
    });

    resizeObserver.observe(textarea);
    return () => resizeObserver.disconnect();
  }, []);

  // Handle content changes
  useEffect(() => {
    adjustTextareaHeight();
  }, [note.content]);

  // Handles focus state and triggers active callback
  const handleFocus = () => {
    setIsFocused(true);
    if (onActive) {
      onActive();
    }
    
    requestAnimationFrame(() => {
      adjustTextareaHeight();
      if (textareaRef.current) {
        textareaRef.current.focus();
        const length = textareaRef.current.value.length;
        textareaRef.current.setSelectionRange(length, length);
      }
    });
  };

  const handleBlur = () => {
    setIsFocused(false);
    if (onInactive) {
      onInactive();
    }
  };

  // New function to render text with hyperlinks
  const renderTextWithLinks = (text) => {
    if (!text) return '';
    
    // Split text into lines to handle bullet points
    const lines = text.split('\n');
    const formattedLines = lines.map((line, lineIndex) => {
      // Handle empty lines
      if (line.trim() === '') {
        return <div key={lineIndex} style={{ height: '1.5em' }}></div>;
      }

      // Check if line starts with a dash for bullet point
      const isBulletPoint = line.trim().startsWith('-');
      const indentLevel = line.search(/\S|$/); // Find first non-whitespace character
      const content = line.trim().replace(/^-/, '').trim(); // Remove the dash and trim

      // Process links within the line content
      const matches = linkify.match(content);
      if (!matches) {
        return (
          <div key={lineIndex} style={{ paddingLeft: `${indentLevel * 1.5}rem` }}>
            {isBulletPoint ? (
              <div className="flex items-start">
                <span className="mr-2">•</span>
                <span>{content}</span>
              </div>
            ) : (
              content
            )}
          </div>
        );
      }

      const elements = [];
      let lastIndex = 0;

      matches.forEach((match, idx) => {
        // Add text before the link
        if (match.index > lastIndex) {
          elements.push(
            <span key={`text-${idx}`}>
              {content.slice(lastIndex, match.index)}
            </span>
          );
        }
        
        // Add the link
        elements.push(
          <Link key={`link-${idx}`} url={match.url} />
        );
        
        lastIndex = match.lastIndex;
      });

      // Add any remaining text after the last link
      if (lastIndex < content.length) {
        elements.push(
          <span key="text-end">
            {content.slice(lastIndex)}
          </span>
        );
      }

      return (
        <div key={lineIndex} style={{ paddingLeft: `${indentLevel * 1.5}rem` }} className="inline">
          {isBulletPoint ? (
            <div className="flex items-start">
              <span className="mr-2">•</span>
              <span className="inline">{elements}</span>
            </div>
          ) : (
            <span className="inline">{elements}</span>
          )}
        </div>
      );
    });

    return formattedLines;
  };

  const handleSimilarityClick = (e) => {
    e.stopPropagation();
    onFindSimilar(note.id);
  };

  // Render note component with textarea and delete button
  return (
    <div 
      data-note-id={note.id}
      className={`
        relative p-4 pl-6 pr-20 border-t border-b flex flex-col
        ${isFocused ? 'bg-gray-50 border-t-black border-b-black' : 'bg-white border-t-gray-200 border-b-white '}
      `}
    >
      {/* Main text content */}
      <div className="flex-1">
        {isFocused ? (
          <textarea
            ref={textareaRef}
            value={editedText}
            onChange={handleTextChange}
            onFocus={handleFocus}
            onBlur={handleBlur}
            className="w-full bg-transparent border-none focus:outline-none resize-none overflow-hidden min-h-0 h-auto whitespace-pre-wrap break-words leading-normal"
            rows="1"
          />
        ) : (
          <div
            onClick={handleFocus}
            className="whitespace-pre-wrap break-words leading-normal cursor-text"
          >
            {renderTextWithLinks(editedText)}
          </div>
        )}
      </div>

      {/* Controls container */}
      <div className="flex items-start gap-2 absolute top-4 right-4">
        {/* Similar notes button */}
        <button 
          onClick={handleSimilarityClick}
          disabled={isDeleting || isSaving}
          className={`
            w-4 h-4
            flex items-center justify-center
            text-gray-400 hover:text-gray-600
            rounded-full hover:bg-gray-100
            transition-colors
            ${(isDeleting || isSaving) ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'}
          `}
        >
          <svg className="h-4 w-4" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path d="M12 4C10.8954 4 10 4.89543 10 6C10 7.10457 10.8954 8 12 8C13.1046 8 14 7.10457 14 6C14 4.89543 13.1046 4 12 4ZM7 12C5.89543 12 5 12.8954 5 14C5 15.1046 5.89543 16 7 16C8.10457 16 9 15.1046 9 14C9 12.8954 8.10457 12 7 12ZM17 12C15.8954 12 15 12.8954 15 14C15 15.1046 15.8954 16 17 16C18.1046 16 19 15.1046 19 14C19 12.8954 18.1046 12 17 12Z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
          </svg>
        </button>

        {/* Delete button and dropdown */}
        <div ref={deleteDropdownRef}>
          <button 
            onClick={handleDeleteClick}
            disabled={isDeleting || isSaving}
            className={`
              w-4 h-4
              flex items-center justify-center
              text-gray-400 hover:text-gray-600
              rounded-full hover:bg-gray-100
              transition-colors
              ${(isDeleting || isSaving) ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'}
            `}
          >
            {(isDeleting || isSaving) ? (
              <svg className="animate-spin h-4 w-4" viewBox="0 0 24 24">
                <circle 
                  className="opacity-25" 
                  cx="12" 
                  cy="12" 
                  r="10" 
                  stroke="currentColor"  
                  strokeWidth="4"
                  fill="none"
                />
                <path 
                  className="opacity-75"
                  fill="currentColor" 
                  d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z"
                />
              </svg>
            ) : (
              <svg className="h-4 w-4 transition-opacity" viewBox="0 0 24 24">
                <path
                  fill="currentColor"
                  d="M19 6.41L17.59 5 12 10.59 6.41 5 5 6.41 10.59 12 5 17.59 6.41 19 12 13.41 17.59 19 19 17.59 13.41 12z"
                />
              </svg>
            )}
          </button>

          {showDeleteDropdown && (
            <div 
              className="absolute w-48 rounded-md shadow-lg bg-white ring-1 ring-black ring-opacity-5 z-10"
              style={getDropdownPosition()}
            >
              <div className="py-1">
                <button
                  onClick={handleConfirmDelete}
                  className="w-full text-left px-4 py-2 text-sm text-red-600 hover:bg-gray-100"
                >
                  Delete
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

export default Note;